import type { OnInit } from '@angular/core';
import {
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import type { OnyxIconName } from '@onyx/angular';
import { OnyxBadgeComponent, OnyxSpinnerComponent } from '@onyx/angular';
import { chain, isEqual } from 'lodash';
import { DateTime } from 'luxon';
import {
  catchError,
  distinctUntilChanged,
  map,
  of,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { OrderProposalStatus } from '../../common/enums/order-proposal-status';
import { OrderProposalType } from '../../common/enums/order-proposal-type';
import { OrderStage } from '../../common/enums/order-stage';
import { OrderStatus } from '../../common/enums/order-status';
import { OrderHelper } from '../../common/helpers/order.helper';
import { OrdersService } from '../../common/services/orders.service';
import type { Order, OrderProposal } from './../../common/interfaces/order';
import { OrderModalStageItemComponent } from './order-modal-stage-item/order-modal-stage-item.component';

@Component({
  selector: 'app-order-modal-stages',
  imports: [
    OnyxBadgeComponent,
    OnyxSpinnerComponent,
    TranslatePipe,
    OrderModalStageItemComponent,
  ],
  templateUrl: './order-modal-stages.component.html',
  styleUrl: './order-modal-stages.component.scss',
})
export class OrderModalStagesComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly ordersService = inject(OrdersService);

  public readonly order = input.required<Order>();

  protected readonly I18N = 'orders.orderModal.orderStages';

  protected readonly OrderStage = OrderStage;

  protected isDropdownAttached = signal(false);
  protected loading = signal(true);
  protected acceptedProposal = signal<OrderProposal | null>(null);

  protected hasManualProposal = computed(
    () =>
      !!OrderHelper.getManualProposal(this.order()) ||
      this.acceptedProposal()?.type === OrderProposalType.MANUAL,
  );

  protected stages = computed(() => {
    const isRouteVerificationLate =
      this.hasApprovedVehicle() &&
      !this.wasRouteVerifiedBeforeApprovedVehicle();

    const proposalStage = this.hasManualProposal()
      ? OrderStage.MANUAL_PROPOSAL
      : this.order().outsourcingData
        ? OrderStage.OUTSOURCING
        : OrderStage.ENGINE_PROPOSAL;

    return chain([
      OrderStage.CREATION,
      !isRouteVerificationLate ? OrderStage.ROUTE_VERIFICATION : null,
      OrderStage.VEHICLE_SEARCH,
      proposalStage,
      isRouteVerificationLate ? OrderStage.ROUTE_VERIFICATION : null,
      OrderStage.REALIZATION,
      OrderStage.COMPLETING_DOCUMENTS,
      this.hasCollectiveInvoice()
        ? OrderStage.WAITING_FOR_COLLECTIVE_INVOICE
        : null,
      OrderStage.INVOICE_TO_ISSUE,
      OrderStage.PAYMENT_CONFIRMATION,
    ])
      .compact()
      .value();
  });
  protected orderStageNumber = computed(() => {
    const status = this.order().status.value;
    if (status === OrderStatus.CANCELED) return 'labels.canceled';

    const STAGE_INDEX: Record<OrderStatus, number> = {
      [OrderStatus.DRAFT]: 1,
      [OrderStatus.VEHICLE_SEARCH]: 2,
      [OrderStatus.TO_ACCEPT]: 3,
      [OrderStatus.TO_DO]: 4,
      [OrderStatus.IN_PROGRESS]: 4,
      [OrderStatus.COMPLETING_DOCUMENTS]: 5,
      [OrderStatus.INVOICE_TO_ISSUE]: 6,
      [OrderStatus.WAITING_FOR_PAYMENT]: 7,
      [OrderStatus.PARTIALLY_PAID]: 7,
      [OrderStatus.PAID]: 8,
      [OrderStatus.CANCELED]: 0,
      [OrderStatus.ARCHIVED]: 8,
    };

    const stage = STAGE_INDEX[status];
    if (stage == null) return null;

    const hasCollectiveInvoice = OrderHelper.hasCollectiveInvoice(this.order());
    const stagesCount = hasCollectiveInvoice ? 9 : 8;
    const offset = hasCollectiveInvoice && stage > 6 ? 1 : 0;

    return `${stage + offset}/${stagesCount}`;
  });
  protected badgeIcon = computed(
    () =>
      (
        ({
          [OrderStatus.DRAFT]: 'edit-filled',
          [OrderStatus.VEHICLE_SEARCH]: 'ai-filled',
          [OrderStatus.TO_ACCEPT]: 'decision-filled',
          [OrderStatus.TO_DO]: 'vehicle-ride-filled',
          [OrderStatus.IN_PROGRESS]: 'vehicle-ride-filled',
          [OrderStatus.COMPLETING_DOCUMENTS]: 'document-filled',
          [OrderStatus.INVOICE_TO_ISSUE]: 'document-filled',
          [OrderStatus.WAITING_FOR_PAYMENT]: 'finance-filled',
          [OrderStatus.PARTIALLY_PAID]: 'finance-filled',
          [OrderStatus.PAID]: 'finance-filled',
          [OrderStatus.ARCHIVED]: 'finance-filled',
          [OrderStatus.CANCELED]: 'clear-filled',
        }) satisfies Record<OrderStatus, OnyxIconName>
      )[this.order().status.value],
  );
  protected hasCollectiveInvoice = computed(() =>
    OrderHelper.hasCollectiveInvoice(this.order()),
  );
  protected hasApprovedVehicle = computed(() =>
    OrderHelper.hasApprovedVehicle(this.order()),
  );

  private activity$ = new Subject<void>();

  public ngOnInit(): void {
    this.activity$
      .pipe(
        map(() => this.order()),
        distinctUntilChanged((previous, current) => isEqual(previous, current)),
        tap(() => this.loading.set(true)),
        switchMap(({ uuid }) =>
          this.ordersService.getOrderActivity(uuid).pipe(
            map(
              ({ proposals }) =>
                proposals?.find(
                  ({ status }) => status === OrderProposalStatus.ACCEPTED,
                ) ?? null,
            ),
            catchError(() => of(null)),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((proposal) => {
        this.acceptedProposal.set(proposal);
        this.loading.set(false);
      });
  }

  protected showDropdown(): void {
    this.isDropdownAttached.set(true);
    this.activity$.next();
  }

  protected hideDropdown(): void {
    this.isDropdownAttached.set(false);
  }

  protected wasRouteVerifiedBeforeApprovedVehicle(): boolean {
    const routeUpdatedAt = this.order().route.updatedAt;
    const approvedVehicleAt = this.order().timestamps.toDoAt;
    if (!routeUpdatedAt || !approvedVehicleAt) return false;

    return (
      DateTime.fromISO(routeUpdatedAt) < DateTime.fromISO(approvedVehicleAt)
    );
  }
}
